import React from "react";
import type { HubEntity, TableRecord } from "../../model/types";
import type { DataSource } from "../../../../types/dataVault.types";
import { formatRelativeCount } from "../../model/formatting";
import { FormField } from "./FormField";
import { DataSourceEntitySelectors } from "./DataSourceEntitySelectors";
import { SubjectAreaField } from "./SubjectAreaField";
import { SqlEditorField, type SqlAutocompleteContext } from "./SqlEditorField";

export function HubMetadataForm({
  hub,
  onFieldChange,
  linkedSatelliteCount,
  linkedLinkCount,
  dataSources,
  dataSourcesLoading,
  dataSourcesError,
  subjectAreaOptions,
  onAddSubjectArea,
  sqlAutocomplete
}: {
  hub: HubEntity;
  onFieldChange: (field: keyof TableRecord, value: string) => void;
  linkedSatelliteCount: number;
  linkedLinkCount: number;
  dataSources: DataSource[];
  dataSourcesLoading: boolean;
  dataSourcesError: string | null;
  subjectAreaOptions: string[];
  onAddSubjectArea: (value: string) => Promise<{ ok: boolean; value: string; error?: string }>;
  sqlAutocomplete?: SqlAutocompleteContext;
}): JSX.Element {
  return (
    <section className="tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-white tw-p-6" style={{paddingTop:"0px"}}>
      <h3 className="tw-text-sm tw-font-semibold tw-text-slate-800">Hub metadata</h3>
      <div className="tw-mt-4 tw-grid tw-gap-4">
        <FormField
          label="Target table"
          value={hub.record.target_table}
          onChange={(value) => onFieldChange("target_table", value)}
          placeholder="e.g., sales_item_h"
        />
        <FormField
          label="Target schema"
          value={hub.record.target_schema}
          onChange={(value) => onFieldChange("target_schema", value)}
          placeholder="e.g., rdv"
        />
        <SubjectAreaField
          value={hub.record.subject_area}
          onChange={(value) => onFieldChange("subject_area", value)}
          options={subjectAreaOptions}
          onAddSubjectArea={onAddSubjectArea}
          disabled={false}
        />
        <FormField
          label="Ensemble"
          value={hub.record.ensemble}
          onChange={(value) => onFieldChange("ensemble", value)}
        />
        <DataSourceEntitySelectors
          dataSources={dataSources}
          loading={dataSourcesLoading}
          error={dataSourcesError}
          selectedDataSource={hub.record.data_source_name ?? ""}
          selectedSourceEntity={hub.record.source_entity_name ?? ""}
          onDataSourceChange={(value, dataSource) => {
            onFieldChange("data_source_name", value);
            onFieldChange("data_source_id", dataSource ? String(dataSource.data_source_id) : "");
            onFieldChange("source_entity_name", "");
            onFieldChange("source_entity_id", "");
          }}
          onSourceEntityChange={(value, sourceEntity) => {
            onFieldChange("source_entity_name", value);
            onFieldChange(
              "source_entity_id",
              sourceEntity ? String(sourceEntity.source_entity_id) : ""
            );
          }}
        />
        <SqlEditorField
          label="Source filter"
          value={hub.record.source_filter ?? ""}
          onChange={(value) => onFieldChange("source_filter", value)}
          placeholder="Enter source filter SQL"
          autocomplete={sqlAutocomplete}
        />
        <SqlEditorField
          label="DQ source filter"
          value={hub.record.dq_source_filter ?? ""}
          onChange={(value) => onFieldChange("dq_source_filter", value)}
          placeholder="Enter data quality filter SQL"
          autocomplete={sqlAutocomplete}
        />
        <FormField
          label="Description"
          value={hub.record.Description ?? ""}
          onChange={(value) => onFieldChange("Description", value)}
          multiline
        />
      </div>
      <div className="tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-100 tw-bg-slate-50 tw-p-3 tw-text-xs tw-text-slate-600">
        <span>{formatRelativeCount(linkedSatelliteCount, "satellite", "satellites")}</span>
        <span>{formatRelativeCount(linkedLinkCount, "link", "links")}</span>
      </div>
    </section>
  );
}
